"use client";

import { Download, FileQuestion } from "lucide-react";
import { useEffect, useRef, useState } from "react";

/**
 * ファイルプレビュー。種別を判定し、レンダラを出し分ける「種別→レンダラのレジストリ」。
 *
 * 設計方針:
 * - ②(ui-kit)は純表示層。presigned 取得は行わず、アプリ側が取得した `blob` を受ける
 *   （blob→objectURL 化のみ内部で担い、URL 直リンクを avoid＝presigned は attachment 固定）。
 * - 画像・PDF・テキストはブラウザ内蔵で完結（追加依存なし）。
 * - docx/xlsx/pptx は `@silurus/ooxml`（Rust/WASM→Canvas）を **動的 import で遅延ロード**。
 *   基底バンドルに WASM を載せない（種別が要求された時だけ取得）。
 * - 未対応種別はアイコン＋ファイル名＋DL のフォールバック。
 * - 外部送出なし: OOXML ビューアは `useGoogleFonts:false` 固定（日本語フォントは自前同梱前提）。
 */

export type FileKind =
    | "image"
    | "pdf"
    | "text"
    | "csv"
    | "docx"
    | "xlsx"
    | "pptx"
    | "unknown";

type Props = {
    /** 表示対象の実体。アプリ側が presigned→blob で取得して渡す。null＝未指定。 */
    blob?: Blob | null;
    /** 拡張子判定・DL 名に使う。 */
    fileName?: string;
    /** MIME。判定は contentType 優先、無ければ拡張子。 */
    contentType?: string;
    /** フォールバック時の DL 動作（未指定なら objectURL への <a download> を使う）。 */
    onDownload?: () => void;
    /**
     * 指定スライドへ送る要求（pptx のみ）。`slideNo` は 1 始まり。
     *
     * `nonce` は同じスライドを続けて要求したときも届かせるための連番で、これが無いと同じ値の
     * 再指定が変化として見えず二度目以降が黙って落ちる。ビューアが未表示・読み込み中の間の
     * 要求は捨てる（プレビューは明示操作でのみ実体を取るため、要求で勝手に取りに行かない）。
     */
    slideRequest?: { slideNo: number; nonce: number } | null;
    className?: string;
};

const IMAGE_EXT = ["png", "jpg", "jpeg", "gif", "webp", "bmp", "svg", "avif"];
const CSV_EXT = ["csv", "tsv"];
const TEXT_EXT = ["txt", "md", "json", "log", "xml", "yaml", "yml"];

/** contentType 優先・拡張子フォールバックでプレビュー種別を判定する（消費側の高さ制御等に公開）。 */
export function detectFileKind(contentType?: string, fileName?: string): FileKind {
    const ct = (contentType ?? "").toLowerCase();
    const ext = (fileName ?? "").toLowerCase().split(".").pop() ?? "";
    if (ct.startsWith("image/") || IMAGE_EXT.includes(ext)) return "image";
    if (ct === "application/pdf" || ext === "pdf") return "pdf";
    if (ct.includes("presentationml") || ext === "pptx") return "pptx";
    if (ct.includes("wordprocessingml") || ext === "docx") return "docx";
    if (ct.includes("spreadsheetml") || ext === "xlsx") return "xlsx";
    // CSV/TSV は表として見せる。text/csv のように text/ で始まるので text 判定より前に振り分ける。
    if (ct.includes("csv") || ct.includes("tab-separated") || CSV_EXT.includes(ext)) return "csv";
    if (ct.startsWith("text/") || TEXT_EXT.includes(ext)) return "text";
    return "unknown";
}

export function FilePreview({
    blob,
    fileName,
    contentType,
    onDownload,
    slideRequest,
    className,
}: Props) {
    const kind = detectFileKind(contentType, fileName);
    const [url, setUrl] = useState<string | null>(null);

    // blob→objectURL（種別に依らず用意。text は blob を直接読むが URL 有無で空状態も判定する）。
    useEffect(() => {
        if (!blob) {
            setUrl(null);
            return undefined; // noImplicitReturns: 末尾がクリーンアップ関数を返すため値を揃える
        }
        const u = URL.createObjectURL(blob);
        setUrl(u);
        return () => URL.revokeObjectURL(u);
    }, [blob]);

    const root = `flex h-full min-h-0 flex-col overflow-hidden rounded border border-border-subtle bg-surface ${className ?? ""}`;

    if (!blob || !url) {
        return (
            <div className={root}>
                <Centered>
                    <span className="text-xs text-text-muted">プレビューなし</span>
                </Centered>
            </div>
        );
    }

    if (kind === "image") {
        return (
            <div className={root}>
                {/* eslint-disable-next-line @next/next/no-img-element */}
                <img src={url} alt={fileName ?? ""} className="h-full w-full object-contain" />
            </div>
        );
    }

    if (kind === "pdf") {
        // ブラウザ内蔵 PDF ビューア。<embed>/<object> は Chromium の blob 不具合があるため <iframe>。
        return (
            <div className={root}>
                <iframe src={url} title={fileName ?? "PDF"} className="h-full w-full border-0" />
            </div>
        );
    }

    if (kind === "text") {
        return (
            <div className={root}>
                <TextPreview blob={blob} />
            </div>
        );
    }

    if (kind === "csv") {
        const tab =
            (contentType ?? "").includes("tab") ||
            (fileName ?? "").toLowerCase().endsWith(".tsv");
        return (
            <div className={root}>
                <CsvPreview blob={blob} delimiter={tab ? "\t" : ","} />
            </div>
        );
    }

    if (kind === "pptx" || kind === "docx" || kind === "xlsx") {
        return (
            <div className={root}>
                <OoxmlViewer kind={kind} src={url} slideRequest={slideRequest} />
            </div>
        );
    }

    // 未対応: アイコン＋名前＋DL フォールバック。
    return (
        <div className={root}>
            <Centered>
                <FileQuestion size={28} className="text-text-muted" aria-hidden />
                <span className="max-w-full truncate text-sm text-text-secondary">
                    {fileName ?? "ファイル"}
                </span>
                <span className="text-xs text-text-muted">この形式はプレビューできません</span>
                {onDownload ? (
                    <button
                        type="button"
                        onClick={onDownload}
                        className="mt-1 inline-flex items-center gap-1 text-sm text-interactive-active hover:underline"
                    >
                        <Download size={14} /> ダウンロード
                    </button>
                ) : (
                    <a
                        href={url}
                        download={fileName ?? "download"}
                        className="mt-1 inline-flex items-center gap-1 text-sm text-interactive-active hover:underline"
                    >
                        <Download size={14} /> ダウンロード
                    </a>
                )}
            </Centered>
        </div>
    );
}

function Centered({ children }: { children: React.ReactNode }) {
    return (
        <div className="flex h-full w-full flex-col items-center justify-center gap-2 p-4 text-center">
            {children}
        </div>
    );
}

/**
 * バイト列を文字列へ復号する。BOM 判定 → UTF-8(fatal)試行 → 失敗なら Shift_JIS(CP932)。
 * 非エンジニアの顧客は Excel 由来の CSV/テキストが多く CP932 の可能性が高いため UTF-8 一択にしない。
 * `TextDecoder` は WHATWG 準拠でブラウザ/WebView2 とも 'shift_jis' を標準サポートする。
 */
function decodeTextBytes(buf: ArrayBuffer): string {
    const b = new Uint8Array(buf);
    if (b[0] === 0xef && b[1] === 0xbb && b[2] === 0xbf) {
        return new TextDecoder("utf-8").decode(b.subarray(3));
    }
    if (b[0] === 0xff && b[1] === 0xfe) return new TextDecoder("utf-16le").decode(b.subarray(2));
    if (b[0] === 0xfe && b[1] === 0xff) return new TextDecoder("utf-16be").decode(b.subarray(2));
    try {
        return new TextDecoder("utf-8", { fatal: true }).decode(b);
    } catch {
        return new TextDecoder("shift_jis").decode(b);
    }
}

/** Blob を復号済み文字列で購読する（先頭 `limit` 文字で打ち切り）。 */
function useDecodedText(blob: Blob, limit: number): string | null {
    const [text, setText] = useState<string | null>(null);
    useEffect(() => {
        let alive = true;
        void blob.arrayBuffer().then((buf) => {
            if (!alive) return;
            const s = decodeTextBytes(buf);
            setText(s.length > limit ? s.slice(0, limit) : s);
        });
        return () => {
            alive = false;
        };
    }, [blob, limit]);
    return text;
}

function TextPreview({ blob }: { blob: Blob }) {
    const text = useDecodedText(blob, 200_000); // 巨大ファイルは頭だけ
    return (
        <pre className="h-full w-full overflow-auto whitespace-pre-wrap break-words p-3 font-mono text-xs text-text-secondary">
            {text ?? "読み込み中…"}
        </pre>
    );
}

/** 区切りテキストを行×列へ。RFC4180 準拠の最小実装（`""` エスケープ・引用内の改行/区切りに対応）。 */
function parseDelimited(text: string, delimiter: string): string[][] {
    const rows: string[][] = [];
    let field = "";
    let row: string[] = [];
    let inQuotes = false;
    for (let i = 0; i < text.length; i++) {
        const ch = text[i];
        if (inQuotes) {
            if (ch === '"') {
                if (text[i + 1] === '"') {
                    field += '"';
                    i++;
                } else inQuotes = false;
            } else field += ch;
        } else if (ch === '"') {
            inQuotes = true;
        } else if (ch === delimiter) {
            row.push(field);
            field = "";
        } else if (ch === "\n") {
            row.push(field);
            rows.push(row);
            row = [];
            field = "";
        } else if (ch !== "\r") {
            field += ch;
        }
    }
    if (field.length > 0 || row.length > 0) {
        row.push(field);
        rows.push(row);
    }
    return rows;
}

/** CSV/TSV をテーブル表示。復号(UTF-8/CP932)後に区切りで解析し、行は上限で打ち切る。 */
function CsvPreview({ blob, delimiter }: { blob: Blob; delimiter: string }) {
    const text = useDecodedText(blob, 2_000_000);
    if (text == null) {
        return <div className="p-3 text-xs text-text-muted">読み込み中…</div>;
    }
    const rows = parseDelimited(text, delimiter).slice(0, 2000); // 大きい CSV は先頭のみ
    if (rows.length === 0) {
        return <div className="p-3 text-xs text-text-muted">データがありません。</div>;
    }
    const cols = rows.reduce((m, r) => Math.max(m, r.length), 0);
    return (
        <div className="h-full w-full overflow-auto">
            <table className="min-w-full border-collapse text-xs">
                <tbody>
                    {rows.map((r, i) => (
                        <tr key={i} className={i === 0 ? "bg-sidebar-bg font-medium" : ""}>
                            {Array.from({ length: cols }, (_, c) => (
                                <td
                                    key={c}
                                    className="whitespace-nowrap border border-border-subtle px-2 py-1 text-text-secondary"
                                >
                                    {r[c] ?? ""}
                                </td>
                            ))}
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
}

/** @silurus/ooxml の命令的ビューアの最小契約（動的 import 後にインスタンスへ束ねる）。 */
type OoxmlHandle = {
    destroy?: () => void;
    goToSlide?: (n: number) => Promise<void>;
    slideIndex?: number;
    slideCount?: number;
    nextPage?: () => Promise<void>;
    prevPage?: () => Promise<void>;
};

function OoxmlViewer({
    kind,
    src,
    slideRequest,
}: {
    kind: "docx" | "xlsx" | "pptx";
    src: string;
    slideRequest?: { slideNo: number; nonce: number } | null;
}) {
    const hostRef = useRef<HTMLDivElement>(null);
    const viewerRef = useRef<OoxmlHandle | null>(null);
    const [status, setStatus] = useState<"loading" | "ready" | "error">("loading");
    const [err, setErr] = useState("");
    const [nav, setNav] = useState<{ index: number; count: number } | null>(null);

    useEffect(() => {
        let disposed = false;
        let viewer: OoxmlHandle | null = null;
        const host = hostRef.current;
        if (!host) return undefined; // noImplicitReturns: 末尾がクリーンアップ関数を返すため値を揃える
        host.innerHTML = "";
        setStatus("loading");
        setNav(null);

        void (async () => {
            try {
                const width = host.clientWidth || 960;
                const onError = (e: Error) => {
                    if (!disposed) {
                        setErr(e.message);
                        setStatus("error");
                    }
                };
                if (kind === "pptx") {
                    const canvas = document.createElement("canvas");
                    canvas.className = "max-w-full";
                    host.appendChild(canvas);
                    const { PptxViewer } = await import("@silurus/ooxml/pptx");
                    if (disposed) return;
                    const v = new PptxViewer(canvas, { useGoogleFonts: false, width, onError });
                    viewer = v as unknown as OoxmlHandle;
                    await v.load(src);
                    if (disposed) return v.destroy();
                    setNav({ index: v.slideIndex, count: v.slideCount });
                } else if (kind === "docx") {
                    const canvas = document.createElement("canvas");
                    canvas.className = "max-w-full";
                    host.appendChild(canvas);
                    const { DocxViewer } = await import("@silurus/ooxml/docx");
                    if (disposed) return;
                    const v = new DocxViewer(canvas, { useGoogleFonts: false, width, onError });
                    viewer = v as unknown as OoxmlHandle;
                    await v.load(src);
                    if (disposed) return v.destroy();
                } else {
                    const div = document.createElement("div");
                    div.style.width = "100%";
                    div.style.height = "100%";
                    host.appendChild(div);
                    const { XlsxViewer } = await import("@silurus/ooxml/xlsx");
                    if (disposed) return;
                    const v = new XlsxViewer(div, { useGoogleFonts: false });
                    viewer = v as unknown as OoxmlHandle;
                    await v.load(src);
                }
                if (disposed) {
                    viewer?.destroy?.();
                    return;
                }
                viewerRef.current = viewer;
                setStatus("ready");
            } catch (e) {
                if (!disposed) {
                    setErr(e instanceof Error ? e.message : String(e));
                    setStatus("error");
                }
            }
        })();

        return () => {
            disposed = true;
            try {
                viewer?.destroy?.();
            } catch {
                /* noop */
            }
            viewerRef.current = null;
        };
    }, [kind, src]);

    // 外からのスライド指定。読み込みが済んでから当てる（読み込み中の要求は捨てる）。
    const nonce = slideRequest?.nonce ?? null;
    const requestedNo = slideRequest?.slideNo ?? null;
    useEffect(() => {
        if (kind !== "pptx" || status !== "ready" || nonce === null || requestedNo === null) return;
        const v = viewerRef.current;
        if (!v || v.goToSlide == null || v.slideCount == null) return;
        // 1 始まりで受けて 0 始まりへ落とす。範囲外は端で止める（要求で表示を壊さない）。
        const index = Math.min(Math.max(requestedNo - 1, 0), Math.max(v.slideCount - 1, 0));
        void (async () => {
            await v.goToSlide?.(index);
            setNav({ index: v.slideIndex ?? index, count: v.slideCount ?? 0 });
        })();
    }, [kind, status, nonce, requestedNo]);

    const goSlide = async (delta: number) => {
        const v = viewerRef.current;
        if (!v || v.goToSlide == null || v.slideCount == null || v.slideIndex == null) return;
        await v.goToSlide(v.slideIndex + delta);
        setNav({ index: v.slideIndex ?? 0, count: v.slideCount ?? 0 });
    };
    const goPage = async (dir: "next" | "prev") => {
        const v = viewerRef.current;
        if (!v) return;
        if (dir === "next") await v.nextPage?.();
        else await v.prevPage?.();
    };

    return (
        <div className="flex h-full min-h-0 flex-col">
            <div ref={hostRef} className="min-h-0 flex-1 overflow-auto bg-page-bg p-2" />
            {status === "loading" && (
                <div className="px-2 py-1 text-xs text-text-muted">読み込み中…</div>
            )}
            {status === "error" && (
                <div className="px-2 py-1 text-xs text-danger">表示に失敗しました: {err}</div>
            )}
            {status === "ready" && kind === "pptx" && nav && nav.count > 1 && (
                <Toolbar>
                    <NavBtn onClick={() => void goSlide(-1)} disabled={nav.index <= 0}>
                        前へ
                    </NavBtn>
                    <span className="text-xs text-text-secondary">
                        {nav.index + 1} / {nav.count}
                    </span>
                    <NavBtn onClick={() => void goSlide(1)} disabled={nav.index >= nav.count - 1}>
                        次へ
                    </NavBtn>
                </Toolbar>
            )}
            {status === "ready" && kind === "docx" && (
                <Toolbar>
                    <NavBtn onClick={() => void goPage("prev")}>前ページ</NavBtn>
                    <NavBtn onClick={() => void goPage("next")}>次ページ</NavBtn>
                </Toolbar>
            )}
        </div>
    );
}

function Toolbar({ children }: { children: React.ReactNode }) {
    return (
        <div className="flex shrink-0 items-center justify-center gap-3 border-t border-border-subtle px-2 py-1.5">
            {children}
        </div>
    );
}

function NavBtn({
    children,
    onClick,
    disabled,
}: {
    children: React.ReactNode;
    onClick: () => void;
    disabled?: boolean;
}) {
    return (
        <button
            type="button"
            onClick={onClick}
            disabled={disabled}
            className="rounded border border-border-subtle px-2 py-0.5 text-xs text-text-secondary hover:bg-sidebar-bg disabled:opacity-40"
        >
            {children}
        </button>
    );
}
